import { supabase } from '../lib/supabase';

/**
 * Service for managing generated documents and their relationship with AI requests
 */
class DocumentService {
  /**
   * Get documents with their AI request status
   * @param {string} projectId - Project ID to filter documents
   * @returns {Promise<Array>} Documents with request status
   */
  async getDocumentsWithRequestStatus(projectId) {
    try {
      const { data, error } = await supabase
        .from('documents_with_request_status')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching documents with status:', error);
      throw error;
    }
  }

  /**
   * Get a single document by ID
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Document data
   */
  async getDocumentById(documentId) {
    try {
      const { data, error } = await supabase
        .from('generated_documents')
        .select('*, ai_requests(*)')
        .eq('id', documentId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching document:', error);
      throw error;
    }
  }

  /**
   * Get documents created by a specific AI request
   * @param {string} requestId - AI request ID
   * @returns {Promise<Array>} Documents created by this request
   */
  async getDocumentsByRequest(requestId) {
    try {
      const { data, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('ai_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching documents by request:', error);
      throw error;
    }
  }

  /**
   * Update document content
   * @param {string} documentId - Document ID
   * @param {string} content - New content
   * @returns {Promise<Object>} Updated document
   */
  async updateDocument(documentId, content) {
    try {
      const { data, error } = await supabase
        .from('generated_documents')
        .update({ 
          content,
          updated_at: new Date().toISOString()
        })
        .eq('id', documentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  /**
   * Delete document
   * @param {string} documentId - Document ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteDocument(documentId) {
    try {
      const { error } = await supabase
        .from('generated_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  /**
   * Get document generation statistics for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Statistics
   */
  async getDocumentStats(projectId) {
    try {
      const { data, error } = await supabase
        .from('documents_with_request_status')
        .select('request_status')
        .eq('project_id', projectId);

      if (error) throw error;

      const stats = {
        total: data.length,
        completed: data.filter(d => d.request_status === 'completed').length,
        processing: data.filter(d => d.request_status === 'processing').length,
        failed: data.filter(d => d.request_status === 'failed').length,
        manual: data.filter(d => !d.request_status).length, // No AI request (manually created)
      };

      return stats;
    } catch (error) {
      console.error('Error fetching document stats:', error);
      throw error;
    }
  }
}

const documentService = new DocumentService();
export default documentService;
